import { useId, useRef, useState } from "react";

/**
 * WAI-ARIA tabs for `count` tabs: the active index plus the props for each tab button and
 * each panel. Arrow keys move along the tabs, Home/End jump to the ends, and selection
 * follows focus. Spread `tabProps(i)` on a <button> and `panelProps(i)` on its panel.
 */
export function useTabs(count) {
  const [active, setActive] = useState(0);
  const tabs = useRef([]);
  const base = useId();

  const select = (i) => {
    const next = (i + count) % count;
    setActive(next);
    tabs.current[next]?.focus();
  };
  const onKeyDown = (e) => {
    const moves = { ArrowRight: active + 1, ArrowLeft: active - 1, Home: 0, End: count - 1 };
    if (!(e.key in moves)) return;
    e.preventDefault();
    select(moves[e.key]);
  };

  const tabProps = (i) => ({
    ref: (el) => {
      tabs.current[i] = el;
    },
    type: "button",
    role: "tab",
    id: `${base}-tab-${i}`,
    "aria-selected": i === active,
    "aria-controls": `${base}-panel-${i}`,
    tabIndex: i === active ? 0 : -1,
    onClick: () => setActive(i),
    onKeyDown,
  });
  const panelProps = (i) => ({
    role: "tabpanel",
    id: `${base}-panel-${i}`,
    "aria-labelledby": `${base}-tab-${i}`,
    hidden: i !== active,
  });

  return { active, tabProps, panelProps };
}
